import { idbAvailable, idbDelete, idbGet, idbSet } from '../../lib/idb';
import { downloadSharedBlob, uploadSharedBlob } from './sharedStore';

/**
 * Binary storage for recordings, chunks and portraits. IndexedDB when available,
 * memory otherwise. A blob that could not be written to IndexedDB is kept in
 * memory for this session, but storage is *not* abandoned: every later call
 * still tries IndexedDB first, so one hiccup never hides every photo until reload.
 */
const mem = new Map<string, Blob>();
const persistent = idbAvailable();

/** The device's storage refused the write: the caller must report it rather than claim "saved". */
export class StorageError extends Error {
  constructor(message = 'storage') {
    super(message);
    this.name = 'StorageError';
  }
}

export async function putBlob(key: string, blob: Blob): Promise<void> {
  if (!persistent) {
    mem.set(key, blob);
  } else {
    try {
      await idbSet('blobs', key, blob);
      mem.delete(key);
    } catch (e) {
      // Never keep it only in memory and say it was saved — it would be gone on the next load.
      console.error(`blob ${key} could not be written to IndexedDB`, e);
      throw new StorageError();
    }
  }
  // Development: the rest of the family gets it from the shared store (see sharedStore.ts).
  await uploadSharedBlob(key, blob);
}

/** Not on this device: fetch it from the shared store once, and keep a copy. */
async function fromShared(key: string): Promise<Blob | undefined> {
  const blob = await downloadSharedBlob(key);
  if (!blob) return undefined;
  if (persistent) {
    try {
      await idbSet('blobs', key, blob);
    } catch {
      /* shown this time, fetched again next time */
    }
  } else {
    mem.set(key, blob);
  }
  return blob;
}

/**
 * Anything at or below this is copied out of IndexedDB into an ordinary blob of
 * its own on the way back; anything larger is handed over as it came.
 *
 * Why copy at all: a Blob read out of IndexedDB is a *handle* on the browser's
 * own storage, not the bytes. Chrome can let that backing go when the database
 * connection is dropped and reopened — which happens on this very app when a
 * second tab opens it (`onversionchange` closes the first), or when `withDb`
 * recovers from a stale connection. Any object url already made from such a
 * handle then fails to load, and an `<img>` pointed at it shows the browser's
 * broken-picture mark: a photograph that is perfectly safe in storage, drawn as
 * an error. Reading the bytes once detaches the copy from all of that.
 *
 * Why only the small ones: every portrait, thumbnail and shared photograph is a
 * few hundred kilobytes at most, so the copy is free and the safety is worth
 * having. A video or an hour of a grandmother's voice can be tens of megabytes,
 * and pulling that into memory to look at a first frame would cost more than the
 * problem does.
 */
const DETACH_MAX_BYTES = 8 * 1024 * 1024;

export async function getBlob(key: string): Promise<Blob | undefined> {
  const inMemory = mem.get(key);
  if (inMemory || !persistent) return inMemory?.size ? inMemory : fromShared(key);
  try {
    const stored = (await idbGet<Blob>('blobs', key)) ?? undefined;
    // A zero-byte blob is a write that went wrong, not a picture. Treated as
    // missing so whatever wants it can make it again (see getThumbUrl) rather
    // than handing an <img> a url with nothing behind it.
    if (!stored || stored.size === 0) return fromShared(key);
    if (stored.size > DETACH_MAX_BYTES) return stored;
    try {
      return new Blob([await stored.arrayBuffer()], { type: stored.type });
    } catch (e) {
      // The handle went stale between the read and here: say so rather than
      // returning something that cannot be drawn.
      console.warn(`blob ${key} could not be read back`, e);
      return undefined;
    }
  } catch (e) {
    console.warn(`blob ${key} could not be read from IndexedDB`, e);
    return undefined;
  }
}

export async function deleteBlob(key: string): Promise<void> {
  mem.delete(key);
  if (persistent) {
    try {
      await idbDelete('blobs', key);
    } catch {
      /* ignore */
    }
  }
}

export function chunkKey(draftId: string, index: number): string {
  return `chunk/${draftId}/${index.toString().padStart(5, '0')}`;
}

export function mediaKey(mediaId: string): string {
  return `media/${mediaId}`;
}

/** The small square kept beside a picture, for the circles on a constellation. */
export function thumbKey(photoId: string): string {
  return `thumb/${photoId}`;
}

/** The picture one size down from itself, uncropped — for the top of a card (see `PhotoRepository.getThumbUrl`). */
export function mediumKey(photoId: string): string {
  return `thumb/m/${photoId}`;
}

/** The same, for a face on a circle — drawn small in the tree and on every row that names somebody. */
export function portraitThumbKey(portraitId: string): string {
  return `thumb/portrait/${portraitId}`;
}
